import React, { memo, useCallback, useEffect, useMemo, useRef } from "react";
import AnswerBox from "./AnswerBox";
import { useQuestionFigures } from "./useQuestionFigures";

/**
 * The answer box as the paper needs it.
 *
 * `AnswerBox` knows nothing about papers, marking or uploads. This adapter
 * holds everything that is true of SolvePaper and nothing that is true of the
 * box, so the box stays reusable — a classroom quiz or a worked example can
 * mount it with a different adapter.
 *
 * Images travel as `{ name, url }`, where a url may be a data url or one the
 * server already has. The page turns them into files at submit time, so
 * nothing is uploaded until an answer is actually sent.
 */
/** How long the typing has to stop before a draft is written. */
const AUTOSAVE_PAUSE_MS = 1200;

const SolveAnswerBox = ({
  questionGuid,
  questionNumber,
  questions,
  images,
  partLabelOf,
  existingAnswerText = "",
  existingImages = [],
  attachedFigures = null,
  onSubmitAnswer,
  /** Persist a draft without marking it. Given only where saving a draft
   *  means something — a classroom assignment, not a practice paper. */
  onAutoSave,
  setAnswers,
  readOnly = false,
  isDark = false,
  isSubmitting = false,
  onInsertFormula,
  /** The marker's verdict, shown under a handed-in answer. */
  feedback,
  /** The pen the toolbar is set to. */
  penColour,
}) => {
  const figures = useQuestionFigures({ questions, questionNumber, images, partLabelOf });

  const initialImages = useMemo(
    () => existingImages.map((url) => ({ url: encodeURI(url), name: url })),
    [existingImages]
  );

  /**
   * Keep the page's copy of this answer current.
   *
   * A whole-paper submit reads from there rather than from the boxes, so an
   * answer the student never pressed submit on still goes with the paper.
   */
  /**
   * Keep the page's copy of this answer current, and put it somewhere it
   * will survive a reload.
   *
   * Only the page's copy was being kept, which lives in memory: a student
   * who wrote a paragraph and refreshed lost it, and whether it came back
   * depended on whether the server happened to have an older draft. The
   * write is held back until the typing pauses, so a sentence is one
   * request rather than forty.
   */
  const saveTimer = useRef(null);
  const lastSaved = useRef(null);

  const handleChange = useCallback(
    (guid, { text, images: collected }) => {
      setAnswers?.((prev) => ({
        ...prev,
        [guid]: { ...prev[guid], text, uploadedImages: collected, annotations: [] },
      }));

      if (!onAutoSave) return;
      const signature = `${text}\u0000${collected.length}`;
      // Claimed before the wait, not after it. Storing the answer's state
      // only once the write had happened meant an unchanged answer looked
      // new each time it was reported, and a report arrives on every render
      // — so the wait was restarted forever and nothing was ever written.
      if (signature === lastSaved.current) return;
      lastSaved.current = signature;

      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => onAutoSave(guid, text, collected), AUTOSAVE_PAUSE_MS);
    },
    [setAnswers, onAutoSave]
  );

  // A draft in flight when the student navigates away is a draft lost.
  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const handleSubmit = useCallback(
    (guid, text, collected) => onSubmitAnswer?.(guid, text, collected),
    [onSubmitAnswer]
  );

  // A drawing pushed across from the question itself (the bar under a figure)
  // arrives as a prop rather than through the menu, so it is applied once per
  // stamp and a student who removed it does not get it back on every render.
  const boxRef = useRef(null);
  const appliedRef = useRef({});
  useEffect(() => {
    if (!boxRef.current) return;
    const current = attachedFigures || {};

    // Arrivals: applied once per stamp, so a figure the student removed does
    // not come back on every render.
    Object.entries(current).forEach(([figureGuid, snapshot]) => {
      if (!snapshot?.image || appliedRef.current[figureGuid] === snapshot.at) return;
      appliedRef.current[figureGuid] = snapshot.at;
      boxRef.current.addImage({
        kind: "figure",
        image: snapshot.image,
        label: snapshot.label,
        // Carried as strokes so they stay editable, and as fractions of the
        // figure so they land correctly at whatever size it is drawn.
        strokes: snapshot.strokes || [],
        name: `figure-${figureGuid}.png`,
      });
    });

    // Departures: taking a figure back out of a part has to reach the box,
    // or unattaching would change nothing a student could see.
    Object.keys(appliedRef.current).forEach((figureGuid) => {
      if (current[figureGuid]) return;
      delete appliedRef.current[figureGuid];
      boxRef.current.removeImageNamed(`figure-${figureGuid}.png`);
    });
  }, [attachedFigures]);

  return (
    <AnswerBox
      ref={boxRef}
      questionGuid={questionGuid}
      figures={figures}
      initialText={existingAnswerText}
      initialImages={initialImages}
      readOnly={readOnly}
      isDark={isDark}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit}
      onChange={handleChange}
      onInsertFormula={onInsertFormula}
      feedback={feedback}
      penColour={penColour}
    />
  );
};

export default memo(SolveAnswerBox);
